import React, { ReactNode } from 'react'
import { DragHandle } from '@mui/icons-material'
import { TableBody, TableRow, TableCell } from '@mui/material'
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
  DraggingStyle,
  NotDraggingStyle,
  DroppableProvided,
  DroppableStateSnapshot,
  DraggableProvided,
  DraggableStateSnapshot
} from 'react-beautiful-dnd'

interface ItemStyleProps {
  snapshot: DraggableStateSnapshot
  draggableStyle: DraggingStyle | NotDraggingStyle
  background?: string
}

export const getItemStyle = ({
  snapshot,
  draggableStyle,
  background
}: ItemStyleProps): React.CSSProperties => {
  return {
    ...draggableStyle,
    background,
    display: snapshot.isDragging ? 'table' : '',
    left: 'auto !important' as any,
    top: draggableStyle.top,
    boxShadow: snapshot.isDragging ? '0 5px 18px 5px rgba(0, 0, 0, 0.25)' : ''
  }
}

interface DroppableComponentProps {
  onDragEnd: (result: DropResult) => void
  additionalRow?: ReactNode
  children: ReactNode
}

export const DroppableComponent =
  ({ onDragEnd, additionalRow }: Omit<DroppableComponentProps, 'children'>) =>
  ({ children }: { children: ReactNode }) => {
    return (
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={'droppable-list-array'} direction="vertical">
          {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => {
            return (
              <TableBody
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={{
                  backgroundColor: snapshot.isDraggingOver
                    ? 'lightgrey'
                    : '#fff'
                }}
              >
                {children}
                {provided.placeholder}
                {additionalRow}
              </TableBody>
            )
          }}
        </Droppable>
      </DragDropContext>
    )
  }

interface DraggableComponentProps {
  draggableId: string
  index: number
  className?: string
  background?: string
  dragHandleVisible?: boolean
  dragHandleDisabled?: boolean
  style?: React.CSSProperties
  onRowClick?: () => void
  children: ReactNode
}

export const DraggableComponent =
  ({
    draggableId,
    index,
    className,
    background = '#fff',
    dragHandleVisible = true,
    dragHandleDisabled = false,
    style = {},
    onRowClick
  }: Omit<DraggableComponentProps, 'children'>) =>
  ({ children }: { children: ReactNode }) => {
    return (
      <Draggable
        draggableId={draggableId}
        index={index}
        isDragDisabled={dragHandleDisabled}
      >
        {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
          return (
            <TableRow
              ref={provided.innerRef}
              {...provided.draggableProps}
              style={{
                ...getItemStyle({
                  snapshot,
                  draggableStyle: provided.draggableProps.style!,
                  ...(!className && { background })
                }),
                ...style
              }}
              className={className}
              hover={false}
              onClick={onRowClick}
            >
              <TableCell {...provided.dragHandleProps} style={{ width: 0 }}>
                {dragHandleVisible && (
                  <DragHandle
                    color={dragHandleDisabled ? 'disabled' : 'primary'}
                  />
                )}
              </TableCell>

              {children}
            </TableRow>
          )
        }}
      </Draggable>
    )
  }

interface DragEndProps<T> {
  result: DropResult
  items: T[]
  setItems: (items: T[]) => void
  patchData?: (
    id: string,
    data: { order: number; position: 'below' | 'above' }
  ) => Promise<any>
  mutateData?: () => Promise<any>
  setDisableDrag?: (disabled: boolean) => void
}

export const onDragEnd = async <T extends { order: number }>({
  result,
  items,
  setItems,
  patchData = () => Promise.resolve(null),
  mutateData = () => Promise.resolve(null),
  setDisableDrag = () => null
}: DragEndProps<T>) => {
  if (!result.destination) return

  setDisableDrag(true)
  const backupList = [...items]
  const listCopy = [...items]

  const newOrderId = listCopy[result.destination.index].order
  const oldOrderId = listCopy[result.source.index].order

  const [removedElement] = listCopy.splice(result.source.index, 1)
  listCopy.splice(result.destination.index, 0, removedElement)

  setItems(listCopy)

  const response = await patchData(result.draggableId, {
    order: newOrderId,
    position: newOrderId > oldOrderId ? 'below' : 'above'
  })
  if (!response || (response?.status !== 200 && response?.status !== 201)) {
    setItems(backupList)
    setDisableDrag(false)
    return
  }

  if (mutateData) {
    await mutateData()
  }
  setDisableDrag(false)
}
